import { Routes, Route, Navigate } from "react-router-dom";
import { ActionsPage } from "@main/pages/ActionsPage";
import { GuidesPage } from "@main/pages/GuidesPage";
import { MoreToolsPage } from "@main/pages/MoreToolsPage";
import { ActivityLogPage } from "@main/pages/ActivityLog";
import { OperatingParametersPage } from "@main/pages/OperatingParametersPage";
import { AuditorFeedbackPage } from "@main/pages/AuditorFeedback";
import { ComplianceCheckPage } from "@main/pages/ComplianceCheck";
import { ConnectGooglePage } from "@main/pages/ConnectGoogle";
import { ConnectM365Page } from "@main/pages/ConnectM365";
import { ConnectStoragePage } from "@main/pages/ConnectStorage";
import { ConnectionsPage } from "@main/pages/Connections";
import { ConsultancyTimePage } from "@main/pages/ConsultancyTime";
import { ContinuousGuidancePage } from "@main/pages/ContinuousGuidance";
import { CyberEssentialsPage } from "@main/pages/CyberEssentials";
import { CyberEssentialsRoute } from "@main/components/standards/StandardGate";
import { DocumentsPage } from "@main/pages/Documents";
import { EvidenceExportPage } from "@main/pages/EvidenceExport";
import { ExecutiveReportPage } from "@main/pages/ExecutiveReport";
import { HelpPage } from "@main/pages/Help";
import { PlaybooksPage } from "@main/pages/help/PlaybooksPage";
import { LibraryPage } from "@main/pages/help/LibraryPage";
import { AskRegistersPage } from "@main/pages/AskRegisters";
import { LegalCompliancePage } from "@main/pages/LegalCompliance";
import { PoliciesPage } from "@main/pages/Policies";
import { PolicyTemplatesPage } from "@main/pages/PolicyTemplates";
import { ProfilePage } from "@main/pages/ProfilePage";
import { ResiliencePage } from "@main/pages/resilience/ResiliencePage";
import { ResponseRoomPage, ResponseRoomsPage } from "@main/pages/resilience/ResponseRooms";
import { RiskRegisterPage } from "@main/pages/RiskRegister";
import { SchedulePage } from "@main/pages/Schedule";
import { SubmissionsPage } from "@main/pages/SubmissionsPage";
import { TeamPage } from "@main/pages/Team";
import { ThreatAdvisoriesPage } from "@main/pages/ThreatAdvisories";
import { TprmPage } from "@main/pages/Tprm";
import { WhatsNewPage } from "@main/pages/WhatsNew";
import { JsyOverviewPage } from "./pages/JsyOverview";
import { JsyOrgProfilePage } from "./pages/JsyOrgProfile";
import { JsyPackagePage } from "./pages/JsyPackage";
import { JcscPage } from "./pages/Jcsc";
import { PolicyGovernancePage } from "@main/pages/PolicyGovernancePage";
import { MyTasksPage } from "@main/pages/MyTasksPage";
import { AssurancePacksPage } from "@main/pages/AssurancePacksPage";
import { VulnerabilitiesPage } from "@main/pages/VulnerabilitiesPage";
import { ExposurePage } from "@main/pages/ExposurePage";
import { RemediationPage } from "@main/pages/RemediationPage";
import { PeopleLifecyclePage } from "@main/pages/PeopleLifecyclePage";
import { AccessReviewsPage } from "@main/pages/AccessReviewsPage";
import { PolicyExceptionsPage } from "@main/pages/PolicyExceptionsPage";
import { ChangeRegisterPage } from "@main/pages/ChangeRegisterPage";
import { CustomerQuestionnairesPage } from "@main/pages/CustomerQuestionnairesPage";
import { DataDiscoveryPage } from "@main/pages/DataDiscoveryPage";
import { ImprovementRegisterPage } from "@main/pages/ImprovementRegister";
import { AiSystemsPage } from "@main/pages/AiSystemsPage";

// ── Jersey edition routes ─────────────────────────────────────────────────
// The main platform's pages wherever the Jersey edition keeps the feature, the
// Jersey app's own pages where the main page would offer what the edition
// leaves out (My Organisation's operating locations and DPJL tier, My Services'
// standards and add-ons). Anything else lands on What's next: the API refuses
// the areas the edition does not carry (backend/src/lib/editions.ts) anyway.

export function JsyRoutes() {
  return (
    <Routes>
      {/* Home is What's next, as on the main platform. */}
      <Route index element={<Navigate to="actions" replace />} />
      <Route path="dashboard" element={<JsyOverviewPage />} />
      <Route path="actions" element={<ActionsPage />} />
      <Route path="guides" element={<GuidesPage />} />
      <Route path="more-tools" element={<MoreToolsPage />} />
      <Route path="compliance" element={<ComplianceCheckPage />} />
      <Route path="executive-report" element={<ExecutiveReportPage />} />
      <Route path="guidance" element={<ContinuousGuidancePage />} />

      {/* A CE+ organisation's links to /cyber-essentials land on its own page (AUD-40). */}
      <Route path="cyber-essentials" element={<CyberEssentialsRoute><CyberEssentialsPage /></CyberEssentialsRoute>} />
      <Route path="cyber-essentials-plus" element={<CyberEssentialsPage plus />} />
      <Route path="auditor-feedback" element={<AuditorFeedbackPage />} />

      <Route path="legal" element={<LegalCompliancePage />} />
      <Route path="submissions" element={<SubmissionsPage />} />
      <Route path="jcsc" element={<JcscPage />} />

      <Route path="risk-register" element={<RiskRegisterPage />} />
      <Route path="tprm" element={<TprmPage />} />
      <Route path="threat-intelligence" element={<ThreatAdvisoriesPage />} />

      <Route path="resilience" element={<ResiliencePage tab="overview" />} />
      <Route path="resilience/incidents" element={<ResiliencePage tab="incidents" />} />
      <Route path="resilience/exercises" element={<ResiliencePage tab="exercises" />} />
      <Route path="resilience/training" element={<ResiliencePage tab="training" />} />
      <Route path="resilience/assets" element={<ResiliencePage tab="assets" />} />
      <Route path="resilience/insurance" element={<ResiliencePage tab="insurance" />} />
      <Route path="resilience/bia" element={<ResiliencePage tab="bia" />} />
      <Route path="resilience/rooms" element={<ResponseRoomsPage />} />
      <Route path="resilience/rooms/:roomId" element={<ResponseRoomPage />} />
      <Route path="vulnerabilities" element={<VulnerabilitiesPage />} />
      <Route path="exposure" element={<ExposurePage />} />
      <Route path="remediation" element={<RemediationPage />} />
      <Route path="data-discovery" element={<DataDiscoveryPage />} />
      {/* Corrective actions raised by a failed CE+ test or an assessor's finding, and shadow AI
          found on the organisation's devices: What's next links both here (AUD-5). */}
      <Route path="improvement" element={<ImprovementRegisterPage />} />
      <Route path="ai-systems" element={<AiSystemsPage />} />

      <Route path="policies" element={<PoliciesPage />} />
      <Route path="templates" element={<PolicyTemplatesPage />} />
      <Route path="documents" element={<DocumentsPage />} />
      <Route path="export" element={<EvidenceExportPage />} />
      <Route path="policy-governance" element={<PolicyGovernancePage />} />
      <Route path="policy-exceptions" element={<PolicyExceptionsPage />} />
      <Route path="changes" element={<ChangeRegisterPage />} />
      <Route path="customer-questionnaires" element={<CustomerQuestionnairesPage />} />
      <Route path="assurance-sharing" element={<AssurancePacksPage />} />
      <Route path="my-tasks" element={<MyTasksPage />} />
      <Route path="people" element={<PeopleLifecyclePage />} />
      <Route path="access-reviews" element={<AccessReviewsPage />} />

      <Route path="org-profile" element={<JsyOrgProfilePage />} />
      <Route path="team" element={<TeamPage />} />
      <Route path="my-package" element={<JsyPackagePage />} />
      <Route path="my-services" element={<Navigate to="../my-package" replace />} />
      <Route path="connections" element={<ConnectionsPage />} />
      <Route path="m365" element={<ConnectM365Page />} />
      <Route path="google" element={<ConnectGooglePage />} />
      <Route path="storage" element={<ConnectStoragePage />} />
      <Route path="schedule" element={<SchedulePage />} />
      <Route path="activity-log" element={<ActivityLogPage />} />
      <Route path="operating-parameters" element={<OperatingParametersPage />} />
      <Route path="profile" element={<ProfilePage />} />

      <Route path="consultancy-time" element={<ConsultancyTimePage />} />
      <Route path="help" element={<HelpPage />} />
      <Route path="playbooks" element={<PolicyTemplatesPage playbooks />} />
      <Route path="help/playbooks" element={<PlaybooksPage />} />
      <Route path="help/playbooks/:playbookId" element={<PlaybooksPage />} />
      <Route path="help/library" element={<LibraryPage />} />
      <Route path="help/library/:docId" element={<LibraryPage />} />
      <Route path="help/ask" element={<AskRegistersPage />} />
      <Route path="whats-new" element={<WhatsNewPage />} />
      <Route path="*" element={<Navigate to="../actions" replace />} />
    </Routes>
  );
}
